import { cast, flow, getEnv, Instance, types } from "mobx-state-tree";

import { UserInviteInput } from "../../api-models/request/UserInput";
import { getFormData } from "../form/FormAppenders";
import { User } from "./User";
import { Column, ColumnType } from "../../components/datatable/interfaces";
import { endpoints } from "../api/ApiEndpoints";
import { UrlParams } from "../../components/datatable/interfaces";
import serializeToQueryParams from "../../helpers/QueryParams";
import { OrganizationContactDetails } from "../organization/Organization";
import { DataGridStorageModel } from "../../components/datatable/DataGridStorage";
import { UserResponse } from "../../api-models/response/user/UserResponse";
import { Option } from "../../components/datatable/interfaces";
import { EmploymentStatusResponse } from "../../api-models/response/EmploymentStatusResponse";
import { Role } from "./Role";
import { currentUserService } from "./CurrentUserStorage";
import { OrganizationInvitationStatuses } from "../../generated/OrganizationInvitations";
import { GridLogicOperator } from "@mui/x-data-grid";
import { moveItemInArray } from "../../helpers/ArrayHelpers";

interface MultipleUsersInput {
  userIds: number[];
}

const UsersModel = types.model({
  userRoleOptions: types.array(types.frozen<Option>()),
  employmentStatusOptions: types.array(types.frozen<Option>()),
  includeDeleted: types.optional(types.boolean, false),
});

export const UsersStorageModel = types
  .compose(DataGridStorageModel, UsersModel)
  .named("UsersStorage")
  .actions(() => {
    return {
      getTranslatedUserStatus(status: OrganizationInvitationStatuses) {
        return `api.organization.invitation.status.${Object.keys(
          OrganizationInvitationStatuses
        ).find((key) => OrganizationInvitationStatuses[key] === status)}.UserTable`;
      },
    };
  })
  .views((self) => ({
    get tableColumns(): Column[] {
      const defaultColumns = [
        {
          key: "name",
          name: "general.user-name",
          type: ColumnType.String,
          sortable: true,
          filterable: true,
          flex: 2,
        },
        {
          key: "status",
          name: "general.users.list-header.employment-status",
          type: ColumnType.SingleSelect,
          sortable: true,
          filterable: true,
          valueOptions: self.employmentStatusOptions,
        },
        {
          key: "organizationInvitationStatus",
          name: "general.status",
          type: ColumnType.SingleSelect,
          valueOptions: [
            {
              label: self.getTranslatedUserStatus(OrganizationInvitationStatuses.Accepted),
              value: OrganizationInvitationStatuses.Accepted.toString(),
            },
            {
              label: self.getTranslatedUserStatus(OrganizationInvitationStatuses.New),
              value: OrganizationInvitationStatuses.New.toString(),
            },
          ],
          sortable: true,
          filterable: true,
        },
        {
          key: "role",
          name: "general.users.list-header.role",
          type: ColumnType.SingleSelect,
          valueOptions: self.userRoleOptions,
          sortable: true,
          filterable: true,
        },
        {
          key: "openJobs",
          name: "general.users.list-header.open-jobs",
          type: ColumnType.Number,
          sortable: true,
          filterable: true,
        },
        {
          key: "totalJobs",
          name: "general.users.list-header.total-jobs",
          type: ColumnType.Number,
          sortable: true,
          filterable: true,
        },
        {
          key: "isOrganizationMember",
          name: "general.users.list-header.organization-member",
          type: ColumnType.SingleSelect,
          valueOptions: [
            {
              label: "general.yes",
              value: "true",
            },
            {
              label: "general.no",
              value: "false",
            },
          ],
          sortable: true,
          filterable: true,
        },
      ];

      if (currentUserService.currentUser.currentOrganization.hasPaidSubscription) {
        defaultColumns.push(
          {
            key: "vaCallsThisCycle",
            name: "general.users.list-header.va-calls-this-cycle",
            type: ColumnType.Number,
            sortable: true,
            filterable: true,
          },
          {
            key: "totalVaCalls",
            name: "general.users.list-header.total-va-calls",
            type: ColumnType.Number,
            sortable: true,
            filterable: true,
          }
        );
      }

      defaultColumns.push(
        {
          key: "email",
          name: "general.users.list-header.email",
          type: ColumnType.String,
          sortable: true,
          filterable: true,
        },
        {
          key: "phone",
          name: "general.users.list-header.phone-number",
          type: ColumnType.String,
          sortable: true,
          filterable: true,
        }
      );

      if(self.includeDeleted){
        const isOrganizationMemberColumnIndex = defaultColumns.findIndex(
          (x) => x.key === "isOrganizationMember"
        );
        moveItemInArray(defaultColumns, isOrganizationMemberColumnIndex, defaultColumns.length - 1);
      }

      return defaultColumns;
    },
  }))
  .actions((self) => {
    return {
      mapItem(item: any) {
        return new User(item);
      },

      setUserRoleOptions(userRoles: Role[]) {
        const userRolesOptions = userRoles.map((role) => {
          return {
            label: role.translationKey,
            value: role.id.toString(),
          };
        });
        self.userRoleOptions = cast(userRolesOptions);
      },

      setEmploymentStatusOptions(employmentStatuses: EmploymentStatusResponse[]) {
        const employmentStatusesOptions = employmentStatuses.map(
          (employmentStatus: EmploymentStatusResponse) => {
            return {
              label: `general.user.employment-status.${employmentStatus.name}`,
              value: employmentStatus.id.toString(),
            };
          }
        );
        self.employmentStatusOptions = cast(employmentStatusesOptions);
      },

      stopCollaborationForUsers: flow(function* stopCollaborationForUsers(ids: MultipleUsersInput) {
        const endpoint = endpoints.get("users");
        const response = yield getEnv(self).api.delete(endpoint, ids);
        self.deselectAll();
        yield self.load();
        return response;
      }),

      inviteUser: flow(function* createUser(user: UserInviteInput) {
        const endpoint = endpoints.get("invite_user");
        const userForm = getFormData<UserInviteInput>(user);
        return yield getEnv(self).api.post(endpoint, userForm);
      }),

      getAdminFilteredUsers: flow(function* (searchText: UrlParams) {
        if (!Object.values(searchText)[0]) return [];
        const endpoint = endpoints.get("users_all");

        searchText["onlyAdmins"] = true;
        endpoint!.queryParams = serializeToQueryParams(searchText);

        const response = yield getEnv(self).api.post(endpoint, {});
        const mappedResponse = response.data.results.map(
          (contactDetails: UserResponse) => new OrganizationContactDetails(contactDetails)
        );
        return mappedResponse;
      }),

      getFilteredUsersExcludingGuest: flow(function* (searchText: string) {
        if (!searchText) return [];
        const endpoint = endpoints.get("users_summary");

        endpoint.queryParams = serializeToQueryParams({ name: searchText, excludeGuest: true });

        return yield getEnv(self).api.get(endpoint);
      }),

      getLockedInspectionsCountForUsers: flow(function* (ids: number[]) {
        const endpoint = endpoints.get("locked_inpsections_count");
        endpoint.queryParams = serializeToQueryParams({
          ids: ids && ids.toString(),
        });
        const response = yield getEnv(self).api.get(endpoint, ids);

        return response;
      }),

      async afterCreate() {
        self.defaultSort = { field: "name", sort: "asc" };
        self.gridColumns = self.tableColumns;

        if (self.includeDeleted) {
          self.defaultFilter = {
            items: [
              {
                field: "isOrganizationMember",
                operator: "is",
                value: "true",
              },
            ],
            logicOperator: GridLogicOperator.And,
          };
          await self.setGridFilter(self.defaultFilter, false);
        }
      },
    };
  })
  .views((self) => ({
    get endpoint() {
      const endpoint = endpoints.get("users_all");
      endpoint.queryParams = self.getDataQuery();
      return endpoint;
    },
    get canLoadItems() {
      return !!self.filterData.contextFilter;
    },
    get selectedOrganizationMembers() {
      const organizationMembers = self.data?.items.filter(
        (item: any) => item.isOrganizationMember
      );
  
      return organizationMembers?.filter((member: any) =>
        self.selectedItems?.includes(member.id.toString())
      );
    }
  }));

export type UsersStorage = Instance<typeof UsersStorageModel>;
